import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useRectificationStore } from '../../../stores';
import { CreateRectificationPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';

const CM_TO_UNIT = 1; // scene units are centimeters

const GlobalReductionHandler = () => {
  const [scene, shadedMesh, material, ghostModeEnabled] = useEditorStore(
    useShallow((s) => {
      const refs = s.sceneRefs || {};
      return [refs.scene, refs.shadedMesh, refs.material, s.ghostModeEnabled];
    })
  );

  const [globalReduction, setGlobalReduction, blockingMask, isApplied] = useRectificationStore(
    useShallow((s) => [s.globalReduction, s.setGlobalReduction, s.blockingZone?.mask, s.isGlobalReductionApplied])
  );

  const previewRef = useRef(null); // preview mesh
  const baseGeoRef = useRef(null); // Holds the pristine, unmodified geometry
  const basePosRef = useRef(null); // Float32Array snapshot of pristine positions
  const baseNorRef = useRef(null); // Float32Array snapshot of pristine normals
  const centerRef = useRef(new THREE.Vector3()); // local space center for scaling/radial offsets
  const lastMeshRef = useRef(null);
  const lastGeometryRef = useRef(null); // track geometry changes for preview invalidation
  const volumeRef = useRef(null);

  /**
   * Computes the bounding box center and the volume of a geometry.
   * @param {THREE.BufferGeometry} geometry The input geometry.
   * @returns {{center: THREE.Vector3, volume: number}} The center and volume.
   */
  function computeCenterAndVolume(geometry) {
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const bb = geometry.boundingBox;
    const center = new THREE.Vector3();
    bb.getCenter(center);

    const pos = geometry.attributes.position;
    const index = geometry.index ? geometry.index.array : null;
    let volume = 0;

    if (index) {
      for (let i = 0; i < index.length; i += 3) {
        const v1 = new THREE.Vector3().fromBufferAttribute(pos, index[i]);
        const v2 = new THREE.Vector3().fromBufferAttribute(pos, index[i + 1]);
        const v3 = new THREE.Vector3().fromBufferAttribute(pos, index[i + 2]);
        volume += v1.dot(v2.clone().cross(v3));
      }
    } else {
      for (let i = 0; i < pos.count; i += 3) {
        const v1 = new THREE.Vector3().fromBufferAttribute(pos, i);
        const v2 = new THREE.Vector3().fromBufferAttribute(pos, i + 1);
        const v3 = new THREE.Vector3().fromBufferAttribute(pos, i + 2);
        volume += v1.dot(v2.clone().cross(v3));
      }
    }
    
    return { center, volume: Math.abs(volume / 6.0) };
  }

  /**
   * Ensures the preview mesh exists in the scene, creating it if necessary.
   * @returns {THREE.Mesh | null} The preview mesh instance.
   */
  function ensurePreview() {
    // Check if geometry has changed (e.g., after alignment transformation)
    const geometryChanged = lastGeometryRef.current &&
      shadedMesh &&
      shadedMesh.geometry.attributes.position !== lastGeometryRef.current;

    // Invalidate cached preview if geometry changed
    if (geometryChanged) {
      if (previewRef.current && previewRef.current.parent) {
        previewRef.current.parent.remove(previewRef.current);
        previewRef.current.geometry?.dispose();
        previewRef.current.material?.dispose();
        previewRef.current = null;
      }
      baseGeoRef.current = null;
      basePosRef.current = null;
      baseNorRef.current = null;
    }

    if (previewRef.current && previewRef.current.parent) return previewRef.current;
    if (!scene || !shadedMesh) return null;

    if (!baseGeoRef.current || lastMeshRef.current !== shadedMesh) {
      baseGeoRef.current = shadedMesh.geometry.clone();
      if (!baseGeoRef.current.attributes.normal) {
        baseGeoRef.current.computeVertexNormals();
      }
      
      const { center, volume } = computeCenterAndVolume(baseGeoRef.current);
      centerRef.current.copy(center);
      volumeRef.current = volume;

      basePosRef.current = baseGeoRef.current.attributes.position.array.slice();
      baseNorRef.current = baseGeoRef.current.attributes.normal.array.slice();

      lastMeshRef.current = shadedMesh;
      lastGeometryRef.current = shadedMesh.geometry.attributes.position; // track current geometry
    }
    
    const cloneGeom = baseGeoRef.current.clone();

    const mat = CreateRectificationPreviewMaterial(material, 1);
    const clone = new THREE.Mesh(cloneGeom, mat);
    clone.name = 'GlobalReductionPreview';
    clone.matrixAutoUpdate = false;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);
    clone.updateMatrix();
    scene.add(clone);

    setGlobalReduction({ previewMesh: clone });

    clone.visible = !!(useRectificationStore.getState().globalReduction?.previewVisible);
    previewRef.current = clone;
    return clone;
  }

  /**
   * Removes the preview mesh from the scene and cleans up resources.
   */
  function disposePreview() {
    const p = previewRef.current;
    if (p && p.parent) {
      p.parent.remove(p);
      p.geometry?.dispose?.();
      p.material?.dispose?.();
    }
    setGlobalReduction({ previewMesh: null });
    previewRef.current = null;
    if (shadedMesh) shadedMesh.visible = true;
  }

  /**
   * Recalculates the vertex positions of the preview mesh based on current settings.
   */
  function rebuildPreview() {
    const preview = ensurePreview();
    if (!preview) return;

    const geom = preview.geometry;
    const outPos = geom.attributes.position;
    const basePositions = basePosRef.current;
    if (!basePositions) return;

    const count = outPos.count;
    const center = centerRef.current;
    const mask = (blockingMask && blockingMask.length === count) ? blockingMask : null;
    const value = parseFloat(globalReduction?.value ?? 0) || 0;
    const type = globalReduction?.valueType || '%';

    if (type === 'cm') {
      // --- REFINED: Invert value for reduction ---
      const delta = -value * CM_TO_UNIT;
      const baseNormals = baseNorRef.current;
      if (!baseNormals) {
        console.error("Base normals not found for 'cm' reduction.");
        return;
      }

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const bx = basePositions[i3 + 0];
        const by = basePositions[i3 + 1];
        const bz = basePositions[i3 + 2];

        // Use smooth weighting from mask instead of hard threshold
        // mask: 0 => fully active, 1 => fully blocked
        const m = mask ? Math.min(1, Math.max(0, mask[i])) : 0;
        const w = 1.0 - m; // active weight

        let dirx = baseNormals[i3 + 0];
        let dirz = baseNormals[i3 + 2];
        
        const len = Math.hypot(dirx, dirz);
        if (len > 1e-8) {
          dirx /= len;
          dirz /= len;
        } else {
          let fallbackDirX = bx - center.x;
          let fallbackDirZ = bz - center.z;
          const fallbackLen = Math.hypot(fallbackDirX, fallbackDirZ);
          if (fallbackLen > 1e-8) {
            dirx = fallbackDirX / fallbackLen;
            dirz = fallbackDirZ / fallbackLen;
          } else {
            dirx = 1; dirz = 0;
          }
        }

        const dx = dirx * delta * w;
        const dz = dirz * delta * w;

        outPos.setXYZ(i, bx + dx, by, bz + dz);
      }
    } else { // Percentage
      // --- REFINED: Invert percentage for reduction ---
      const reductionRatio = value / 100.0;

      // --- REFINED: Compensate using smooth active weights (1 - mask[i]) ---
      let activeWeightSum = count;
      if (mask) {
        let sum = 0;
        for (let i = 0; i < count; i++) {
          const m = Math.min(1, Math.max(0, mask[i]));
          sum += 1.0 - m; // active weight at vertex i
        }
        activeWeightSum = sum;
      }
      const activeRatio = activeWeightSum > 0 ? activeWeightSum / count : 0;
      
      // We need to achieve the total volume reduction using only the active vertices.
      // This is an approximation assuming volume scales linearly with the vertex ratio.
      const adjustedReduction = activeRatio > 0.01 ? reductionRatio / activeRatio : reductionRatio;
      
      // --- REFINED: Horizontal-only scaling. Volume scales with s^2 for XZ scaling ---
      // V_new / V_base = s^2. Target is (1 - adjustedReduction). So s = sqrt(1 - adjustedReduction)
      const scaleTarget = 1.0 - adjustedReduction;
      const s = Math.sqrt(Math.max(0, scaleTarget));

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        const bx = basePositions[i3 + 0];
        const by = basePositions[i3 + 1];
        const bz = basePositions[i3 + 2];

        // Smoothly attenuate scaling using mask weight
        const m = mask ? Math.min(1, Math.max(0, mask[i])) : 0; // 0..1
        const w = 1.0 - m; // active weight 0..1
        const si = 1.0 - (1.0 - s) * w; // per-vertex scale

        // Scale only X and Z relative to the center
        const ox = (bx - center.x) * si;
        const oz = (bz - center.z) * si;
        
        // Keep original Y position
        outPos.setXYZ(i, center.x + ox, by, center.z + oz);
      }
    }

    outPos.needsUpdate = true;
    geom.computeVertexNormals();
    geom.computeBoundingSphere?.();
  }

  useEffect(() => {
    if (!scene || !shadedMesh) return;
    ensurePreview();
    rebuildPreview();
    return () => { disposePreview(); };
  }, [scene, shadedMesh]);

  useEffect(() => {
    const p = previewRef.current;
    if (!p || !shadedMesh) return;
    // Hide preview when tool is applied (finalized) or when preview is explicitly disabled
    const visible = !!(globalReduction?.previewVisible) && !isApplied;
    p.visible = visible;
  }, [globalReduction?.previewVisible, shadedMesh, isApplied]);

  // Adjust preview mesh material based on ghost mode
  useEffect(() => {
    const previewMesh = previewRef.current;

    // Only update if preview mesh exists
    if (!previewMesh || !previewMesh.material) return;

    if (ghostModeEnabled) {
      // Ghost mode ON: Make preview completely opaque
      previewMesh.material.opacity = 1.0;
      previewMesh.material.transparent = false;
    } else {
      // Ghost mode OFF: Make preview transparent
      previewMesh.material.opacity = 0.6;
      previewMesh.material.transparent = true;
    }

    previewMesh.material.needsUpdate = true;
  }, [ghostModeEnabled]);

  useEffect(() => {
    if (!previewRef.current) return;
    rebuildPreview();
  }, [globalReduction?.value, globalReduction?.valueType, blockingMask]);

  useEffect(() => {
    if (lastMeshRef.current !== shadedMesh) {
      baseGeoRef.current = null;
      disposePreview();
      if (scene && shadedMesh) {
        ensurePreview();
        rebuildPreview();
      }
    }
  }, [shadedMesh]);

  return null;
};

export default GlobalReductionHandler;
